import { useState, useEffect, useCallback, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { sendEventPreApprovedMeetingModalClick } from 'shared/lib/sendGAEvents';
import type { TPreApprovalOfferStepData } from 'shared/types/TRankingService';

import { useGetDeliverySlotsMutation } from 'entities/preApprovals';

import { DELIVERY_FORM_FIELDS } from '../constants';

const POLLING_INTERVAL = 5_000;

export const usePollDeliverySlots = (offerData: TPreApprovalOfferStepData) => {
  const [trigger, { data, isError }] = useGetDeliverySlotsMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(true);
  const [isEmptySlots, setIsEmptySlots] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { resetField } = useFormContext();

  const { bankName, productName, preApprovalOfferId, preApprovalStatus } = offerData || {};

  const stopPolling = useCallback(() => {
    setShouldPoll(false);
  }, []);

  const startPolling = useCallback(() => {
    setShouldPoll(true);
  }, []);

  useEffect(() => {
    if (!preApprovalOfferId || !shouldPoll) {
      return;
    }

    let isCancelled = false;
    let activeRequest: ReturnType<typeof trigger> | null = null;

    const clearTimer = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const runPoll = async () => {
      if (isCancelled) {
        return;
      }

      setIsLoading(true);
      setIsEmptySlots(false);
      resetField(DELIVERY_FORM_FIELDS.DATE_SLOT);
      resetField(DELIVERY_FORM_FIELDS.TIME_SLOT);

      activeRequest = trigger({ offerId: preApprovalOfferId });

      try {
        const result = await activeRequest.unwrap();

        if (isCancelled) {
          return;
        }

        if (result.status === 'complete') {
          setShouldPoll(false);
          setIsLoading(false);
          setIsEmptySlots(!result.slots.length);
          return;
        }

        timeoutRef.current = setTimeout(() => {
          runPoll();
        }, POLLING_INTERVAL);
      } catch (err) {
        if (isCancelled) {
          return;
        }

        setIsLoading(false);
        setShouldPoll(false);

        sendEventPreApprovedMeetingModalClick({
          bankName,
          offerName: productName,
          offerStatus: preApprovalStatus || '',
          action: 'Ошибка адреса',
        });
      }
    };

    runPoll();

    return () => {
      isCancelled = true;
      setIsLoading(false);
      setIsEmptySlots(false);
      clearTimer();
      activeRequest?.abort?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preApprovalOfferId, shouldPoll]);

  return {
    data,
    isLoading,
    isEmptySlots,
    isError,
    isPolling: shouldPoll,
    stopPolling,
    startPolling,
  };
};
